// src/components/TTSProofReportModal.jsx
// P0 PROOF REPORT: One-click diagnostic package generator
// No DevTools, no curl, no guessing - single canonical artifact
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Check, Loader2, FileText, AlertCircle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { BACKEND_URL } from '@/config/api';

const VerdictBadge = ({ verdict }) => {
  const configs = {
    'DONE': { color: 'bg-emerald-900/50 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
    'EXECUTING': { color: 'bg-blue-900/50 text-blue-400 border-blue-500/30', icon: Loader2, spin: true },
    'FINALIZING': { color: 'bg-cyan-900/50 text-cyan-400 border-cyan-500/30', icon: Clock },
    'QUEUED_WITH_KICK': { color: 'bg-amber-900/50 text-amber-400 border-amber-500/30', icon: Clock },
    'QUEUED_NO_KICK': { color: 'bg-orange-900/50 text-orange-400 border-orange-500/30', icon: AlertCircle },
    'FAIL': { color: 'bg-red-900/50 text-red-400 border-red-500/30', icon: XCircle },
    'BLOCKED': { color: 'bg-red-900/50 text-red-400 border-red-500/30', icon: XCircle },
    'NO_JOB_RECORD': { color: 'bg-slate-800/50 text-slate-400 border-slate-500/30', icon: AlertCircle },
  };
  
  const config = configs[verdict] || configs['NO_JOB_RECORD'];
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-sm font-medium ${config.color}`}>
      <Icon className={`w-4 h-4 ${config.spin ? 'animate-spin' : ''}`} />
      {verdict}
    </span>
  );
};

const TTSProofReportModal = ({ open, onOpenChange, titleId, titleName }) => {
  const [loading, setLoading] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportJson, setReportJson] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !titleId) return;
    
    const fetchReport = async () => {
      setLoading(true);
      setError('');
      setReportText('');
      setReportJson(null);
      
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(
          `${BACKEND_URL}/api/publisher/titles/${titleId}/tts/proof-report?include_r2_scan=true`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        const data = await res.json().catch(() => ({}));
        
        if (!res.ok || !data?.ok) {
          throw new Error(data?.detail?.error || data?.detail || data?.error || `HTTP_${res.status}`);
        }
        
        setReportText(data.report_text || '');
        setReportJson(data.report_json || null);
      } catch (e) {
        console.error('[TTSProofReport] Error:', e);
        setError(String(e?.message || e));
      } finally {
        setLoading(false);
      }
    };
    
    fetchReport();
  }, [open, titleId]);

  const copyReport = async () => {
    if (!reportText) return;
    await navigator.clipboard.writeText(reportText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-50 max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-400" />
            TTS Proof Report
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {titleName || titleId}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
              <span className="ml-3 text-slate-400">Generating report...</span>
            </div>
          )}
          
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-300 font-medium">Failed to generate report</p>
                <p className="text-red-400 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && reportJson && (
            <>
              {/* Verdict banner */}
              <div className="mb-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Verdict</p>
                    <VerdictBadge verdict={reportJson.verdict} />
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Segments</p>
                    <p className="text-lg font-mono">
                      {reportJson.r2_scan?.present_count ?? '?'} / {reportJson.expected_segments}
                    </p>
                  </div>
                </div>
                {reportJson.verdict_detail && (
                  <p className="text-sm text-slate-400 mt-2">{reportJson.verdict_detail}</p>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 mb-4">
                <Button 
                  onClick={copyReport} 
                  disabled={!reportText}
                  className="bg-indigo-600 hover:bg-indigo-500"
                  data-testid="copy-report-btn"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Report
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => onOpenChange(false)}
                  className="border-slate-600"
                >
                  Close
                </Button>
              </div>

              {/* Report text */}
              <div className="flex-1 overflow-auto">
                <pre className="whitespace-pre-wrap rounded-lg border border-slate-700 bg-slate-950 p-4 text-sm text-slate-300 font-mono">
                  {reportText || '(empty)'}
                </pre>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TTSProofReportModal;
